/**
 * WebSocket terminal bridge
 *
 * Connects a WebSocket to the local terminal in raw mode,
 * piping stdin -> WS and WS -> stdout for an interactive
 * terminal session inside a Cloudflare Sandbox.
 *
 * Includes automatic reconnection with exponential backoff (up to 10
 * attempts) so transient network issues don't kill the session.
 *
 * The container's .bashrc handles all setup (env, cd, clear).
 * This is a pure stdin/stdout bridge — no commands are sent, no output is
 * suppressed. The `clear` in .bashrc wipes any shell noise before the coding agent
 * starts rendering.
 */

import WebSocket from 'ws';

const MAX_RECONNECT = 10;
const BASE_DELAY_MS = 1000;
const HEARTBEAT_INTERVAL_MS = 60_000;

/**
 * Derive the HTTP heartbeat URL from the WebSocket URL.
 * ws(s)://host/sandbox/:id/ws?token=tok → https://host/sandbox/:id/heartbeat?token=tok
 */
function deriveHeartbeatUrl(wsUrl: string): string {
  return wsUrl
    .replace(/^wss:/, 'https:')
    .replace(/^ws:/, 'http:')
    .replace(/\/ws\?/, '/heartbeat?');
}

/**
 * Open a WebSocket connection to the given URL and bridge it to
 * the local terminal (stdin/stdout) in raw mode.
 *
 * Automatically reconnects on unexpected close (up to MAX_RECONNECT attempts).
 * Returns 0 on clean close, throws on unrecoverable error.
 */
export async function connectTerminal(wsUrl: string, sessionId?: string): Promise<number> {
  let reconnectAttempts = 0;
  let intentionalClose = false;

  function connect(): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      const ws = new WebSocket(wsUrl);

      let isRaw = false;

      function enterRawMode(): void {
        if (process.stdin.isTTY && !isRaw) {
          process.stdin.setRawMode(true);
          isRaw = true;
        }
        process.stdin.resume();
      }

      function exitRawMode(): void {
        if (process.stdin.isTTY && isRaw) {
          process.stdin.setRawMode(false);
          isRaw = false;
        }
        process.stdin.pause();
      }

      function onStdinData(data: Buffer): void {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(data);
        }
      }

      function onResize(): void {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            type: 'resize',
            cols: process.stdout.columns || 80,
            rows: process.stdout.rows || 24,
          }));
        }
      }

      function cleanup(): void {
        if (pingInterval) {
          clearInterval(pingInterval);
          pingInterval = null;
        }
        if (heartbeatInterval) {
          clearInterval(heartbeatInterval);
          heartbeatInterval = null;
        }
        process.stdin.removeListener('data', onStdinData);
        process.stdout.removeListener('resize', onResize);
        exitRawMode();
      }

      // Keepalive: ping every 30s to detect dead connections early.
      // Without this, firewalls/proxies can silently drop idle TCP connections
      // and the client won't know until it tries to send data.
      let pingInterval: ReturnType<typeof setInterval> | null = null;

      // HTTP heartbeat: reset the container's sleepAfter timer via an HTTP
      // request to the DO. WebSocket messages don't trigger the DO's
      // renewActivityTimeout — only containerFetch (HTTP) does.
      let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
      const heartbeatUrl = deriveHeartbeatUrl(wsUrl);

      ws.on('open', () => {
        reconnectAttempts = 0;
        enterRawMode();
        process.stdin.on('data', onStdinData);
        process.stdout.on('resize', onResize);

        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.ping();
          }
        }, 30_000);

        // Send heartbeat immediately, then every 60s. Fire-and-forget: a
        // missed heartbeat just means the container's idle timer isn't
        // reset this cycle, not a session-ending failure.
        fetch(heartbeatUrl, { method: 'POST' }).catch((err) => {
          process.stderr.write(`\r\n[heartbeat] ${err instanceof Error ? err.message : String(err)}\r\n`);
        });
        heartbeatInterval = setInterval(() => {
          fetch(heartbeatUrl, { method: 'POST' }).catch((err) => {
            process.stderr.write(`\r\n[heartbeat] ${err instanceof Error ? err.message : String(err)}\r\n`);
          });
        }, HEARTBEAT_INTERVAL_MS);
      });

      ws.on('message', (data: WebSocket.RawData, isBinary: boolean) => {
        if (isBinary) {
          const buf = Buffer.isBuffer(data)
            ? data
            : data instanceof ArrayBuffer
              ? Buffer.from(data)
              : Buffer.concat(Array.from(data));

          process.stdout.write(buf);
          return;
        }

        // Text frames: try to parse as JSON control message, otherwise raw terminal data
        const text = Buffer.isBuffer(data) ? data.toString() : String(data);
        let msg: unknown;
        try {
          msg = JSON.parse(text);
        } catch {
          // If it looks like a JSON control message but failed to parse, warn
          const trimmed = text.trim();
          if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            process.stderr.write(`[Malformed control message: ${trimmed.substring(0, 80)}]\r\n`);
          }
          process.stdout.write(text);
          return;
        }
        if (msg && typeof msg === 'object' && (msg as Record<string, unknown>).type === 'ready') {
          const cols = process.stdout.columns || 80;
          const rows = process.stdout.rows || 24;
          ws.send(JSON.stringify({ type: 'resize', cols, rows }));
        }
      });

      ws.on('close', () => {
        cleanup();

        if (intentionalClose) {
          resolve(0);
          return;
        }

        // Attempt reconnection
        if (reconnectAttempts < MAX_RECONNECT) {
          reconnectAttempts++;
          const delay = BASE_DELAY_MS * Math.pow(2, Math.min(reconnectAttempts - 1, 5));
          const jitter = Math.random() * delay * 0.1;
          process.stderr.write(`\r\n[Connection lost. Reconnecting (${reconnectAttempts}/${MAX_RECONNECT})...]\r\n`);
          setTimeout(() => {
            connect().then(resolve, reject);
          }, delay + jitter);
        } else {
          process.stderr.write('\r\n[Connection lost. Max reconnect attempts reached.]\r\n');
          resolve(1);
        }
      });

      // Only log errors here — don't reconnect. WebSocket always fires
      // 'close' after 'error', so the close handler handles reconnection.
      // Reconnecting from both handlers causes exponential connection
      // duplication (1→2→4→8 parallel connections).
      ws.on('error', (err: Error) => {
        process.stderr.write(`\r\n[Connection error: ${err.message}]\r\n`);
      });
    });
  }

  // Listen for SIGINT to cleanly close without triggering reconnection
  const sigintHandler = () => {
    intentionalClose = true;
  };
  process.once('SIGINT', sigintHandler);

  try {
    return await connect();
  } finally {
    process.removeListener('SIGINT', sigintHandler);
  }
}
